import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { DateTime } from 'luxon';

import { Advert } from 'interfaces/common';
import { UserService, FavoriteService } from 'services/common';
import { ReloadService, UiService } from 'services/core';
import { AdvertReportComponent } from '../advert-report/advert-report.component';

@Component({
  selector: 'app-advert-details-right-area',
  templateUrl: './advert-details-right-area.component.html',
  styleUrls: ['./advert-details-right-area.component.scss'],
})
export class AdvertDetailsRightAreaComponent implements OnInit, OnDestroy {
  @Input() public advert!: Advert;
  public favoriteId?: string = undefined; // if this is defined it means this advert is added to Favorites

  private subReload: Subscription = Subscription.EMPTY;
  private subGetFav: Subscription = Subscription.EMPTY;
  private subAddFav: Subscription = Subscription.EMPTY;
  private subRemoveFav: Subscription = Subscription.EMPTY;

  constructor(
    private reloadService: ReloadService,
    private favoriteService: FavoriteService,
    private uiService: UiService,
    private dialog: MatDialog,
    private userService: UserService,
    private router: Router
  ) {}

  ngOnInit() {
    this.subReload = this.reloadService.refreshFavorite$.subscribe(() =>
      this.setFavoriteId()
    );
    this.setFavoriteId();
  }

  public get advertId() {
    return this.advert?.id;
  }

  public get verifiedTooltip() {
    const date = this.advert?.user?.verifiedAt;
    if (!date) return '';
    return 'Verified on ' + DateTime.fromISO(date).toFormat('yyyy-MM-dd');
  }

  private setFavoriteId() {
    this.subGetFav = this.favoriteService.getFavoriteByUser().subscribe({
      next: res => {
        const favFound = res.data.find(f => f.advert?.id === this.advertId);
        this.favoriteId = favFound?.advert.id;
      },
      error: error => console.log(error),
    });
  }

  private addToFavoriteDB(advertId: string) {
    this.subAddFav = this.favoriteService.addToFavorite(advertId).subscribe({
      next: res => {
        this.uiService.success(res.message);
        this.reloadService.needRefreshFavorite$();
      },
      error: error => console.log(error),
    });
  }

  private removeFavoriteById(id: string) {
    this.subRemoveFav = this.favoriteService.deleteFavoriteById(id).subscribe({
      next: res => {
        this.reloadService.needRefreshFavorite$();
        this.uiService.success(res.message);
      },
      error: error => console.log(error),
    });
  }

  public onAddToFavorite(event: MouseEvent) {
    event.stopPropagation();
    if (this.favoriteId) return this.removeFavoriteById(this.favoriteId);

    if (this.userService.getUserStatus()) {
      this.addToFavoriteDB(this.advertId);
    } else {
      this.router.navigate(['/login']);
      this.reloadService.needRefreshFavorite$();
    }
  }

  /**
   * Dialog Component
   * openAddressDialog()
   */
  public openReportDialog() {
    this.router.navigate([], {
      queryParams: { dialogOpen: true },
      queryParamsHandling: 'merge',
    });
    this.reloadService.needRefreshData$();
    const dialogRef = this.dialog.open(AdvertReportComponent, {
      panelClass: ['theme-dialog'],
      width: '100%',
      maxWidth: '550px',
      maxHeight: '100%',
      autoFocus: false,
      disableClose: false,
      data: this.advert,
    });

    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        dialogRef.close(dialogResult);
        this.router.navigate([], {
          queryParams: { dialogOpen: null },
          queryParamsHandling: 'merge',
        });
      }
    });
  }

  ngOnDestroy() {
    this.subReload.unsubscribe();
    this.subGetFav.unsubscribe();
    this.subAddFav.unsubscribe();
    this.subRemoveFav.unsubscribe();
  }
}
